import React from "react";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  useGridSelector,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  GridColumnIcon,
} from "@mui/x-data-grid-pro";
import type { GridApi, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { useHiddenColumnsCount } from "../hooks";

export interface ColumnsToolbarButtonProps {
  /** apiRef of the DataGridPro this button controls. */
  apiRef: React.MutableRefObject<GridApi>;
  /**
   * The table's default column visibility, used to compute how many columns the user has hidden.
   * Must be a stable, code-defined constant (the table's shipped default) — never derive this from
   * DataTableContainer's restored `initialState`/localStorage value, or the indicator will silently
   * stop reflecting columns hidden before the last save. See useHiddenColumnsCount's docs for why
   * this hook already correctly survives a page reload as long as this stays a fixed constant.
   */
  defaultVisibilityModel: GridColumnVisibilityModel;
}

/**
 * Drop-in replacement for GridToolbarColumnsButton that also indicates when the user has hidden
 * columns. Below 700px it collapses to icon-only (matching the same icon-only treatment already
 * used by PayerTableToolbar's filter/export buttons), with a numbered badge overlaid when columns
 * are hidden. At 700px and up it shows a "Columns (N hidden)" recolored label instead of the
 * badge. Renders as a plain button/icon with no indicator when nothing is hidden.
 */
export function ColumnsToolbarButton({ apiRef, defaultVisibilityModel }: ColumnsToolbarButtonProps): JSX.Element {
  const theme = useTheme();
  // Use a literal 700px threshold rather than the "md" breakpoint key — this app's theme
  // (components/src/themes/themePrimary.tsx) happens to also set "md" to 700px, but a literal
  // number keeps this resilient to future theme changes, matching the same defensive reasoning
  // used for the earlier 600px fix (where "sm" turned out to mean 450px here, not 600px).
  const isIconOnly = useMediaQuery(theme.breakpoints.down(700));
  const hiddenCount = useHiddenColumnsCount(apiRef, defaultVisibilityModel);
  const { open, openedPanelValue } = useGridSelector(apiRef, gridPreferencePanelStateSelector);

  const toggleColumnsPanel = () => {
    if (open && openedPanelValue === GridPreferencePanelsValue.columns) {
      apiRef.current.hidePreferences();
    } else {
      apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
    }
  };

  const baseLabel = apiRef.current.getLocaleText("toolbarColumns");
  const label = isIconOnly ? "" : hiddenCount > 0 ? `${baseLabel} (${hiddenCount} hidden)` : baseLabel;
  const baseAriaLabel = apiRef.current.getLocaleText("toolbarColumnsLabel");
  const ariaLabel = hiddenCount > 0 ? `${baseAriaLabel} (${hiddenCount} hidden)` : baseAriaLabel;

  const button = (
    <Button
      size="small"
      startIcon={<GridColumnIcon />}
      onClick={toggleColumnsPanel}
      aria-label={ariaLabel}
      sx={{
        // Matches the fontSize/fontWeight/letterSpacing/textTransform already applied to the
        // export button (CustomExportButton/GridToolbarExport) in every toolbar that renders this
        // component, so all three toolbar buttons (filter, columns, export) look consistent.
        fontSize: "16px",
        fontWeight: 400,
        letterSpacing: 0.15,
        textTransform: "none",
        ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
        ...(hiddenCount > 0 ? { color: (t: Theme) => t.palette.primary.main } : {}),
      }}
    >
      {label}
    </Button>
  );

  if (hiddenCount > 0 && isIconOnly) {
    return (
      <Badge badgeContent={hiddenCount} color="primary">
        {button}
      </Badge>
    );
  }

  return button;
}
